import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import PreferedCard from '../../../components/PreferedCard/PreferedCard';
import { RadioGroup } from '../../../components/Radio';
import RadioWithImage from '../../../components/RadioWithImage';
import Select from '../../../components/Select';
import TextInput from '../../../components/TextInput/TextInput';
import useMobile from '../../../hooks/useMobileHook';
import {
  formatAddressInTwoLines,
  getAllAddressesMappedByType,
} from '../../../services/addresses';
import { getAllStates } from '../../../services/states';

export type customPensionVerificationLetterModalProps = {
  showModal: boolean;
  handleCloseModal: () => void;
  handleConfirm: (formData: any) => void;
};

const radioCards = [
  {
    imageRef: 'rollover-directly-to-me.png',
    cardTitle: 'Send it to me',
    cardSubtitle: 'Send it to your own address.',
    id: 'rollover-to-me',
  },
  {
    imageRef: 'letter-writing.png',
    cardTitle: 'Send to 3rd Party',
    cardSubtitle: 'A copy will also be mailed to your address.',
    id: 'letter-writing',
  },
];

const PensionVerificationLetter = ({
  showModal,
  handleCloseModal,
  handleConfirm,
}: customPensionVerificationLetterModalProps) => {
  const [radioValue, setRadioValue] = useState<string | number>();
  const addresses = getAllAddressesMappedByType();

  const handleOnRadioChange = (e: string | number) => {
    setRadioValue(e);
  };

  const states = getAllStates();

  const isMobile = useMobile();

  const validationSchema = yup.object().shape({
    thirdPartyName: yup
      .string()
      .required('Third party name is required')
      .max(245, 'Must be no longer than 245 characters'),
    address1: yup
      .string()
      .required('Address 1 is required')
      .max(245, 'Must be no longer than 245 characters'),
    address2: yup.string(),
    city: yup
      .string()
      .required('City is required')
      .max(245, 'Must be no longer than 245 characters'),
    state: yup
      .string()
      .required('State is required')
      .max(245, 'Must be no longer than 245 characters'),
    zip: yup
      .string()
      .required('Zip is required')
      .matches(/^[0-9]+$/, 'Must be only digits')
      .max(5, 'Must be no longer than 5 characters'),
  });

  const address = {
    thirdPartyName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  };

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: address,
    resolver: yupResolver(validationSchema),
  });

  const handleSubmitForm = (formData: any) => {
    handleConfirm(formData);
  };

  return (
    <Modal
      show={showModal}
      onHide={handleCloseModal}
      dialogClassName="modal-xl"
      fullscreen={isMobile}
    >
      <ModalHeader className="border-0" closeButton>
        <h4 className="text-sm-20-lg-32 mb-0">Generate Verification Letter</h4>
      </ModalHeader>
      <ModalBody>
        <section>
          <div>
            <h6 className="m-0 text-sm-16-lg-24">
              Request a letter verifying your pension benefit.
            </h6>
          </div>
          <div className={'mt-7 text-sub1 fw-light lh-xxs'}>
            <span>
              This request generates a letter that is used to verify your
              current benefit amount, form of payment, and future benefit
              changes.
            </span>
          </div>
        </section>
        <section className="mt-13">
          <RadioGroup
            name={'choice'}
            value={radioValue}
            onChange={handleOnRadioChange}
          >
            <Row>
              {radioCards.map((radioCard, idx) => {
                return (
                  <Col key={idx} className={'col-12 col-lg-3 mb-11 pb-1'}>
                    <RadioWithImage
                      className="h-100"
                      variant="transperant"
                      cardData={radioCard}
                      selected={radioValue !== radioCard.id}
                    />
                  </Col>
                );
              })}
            </Row>
          </RadioGroup>
        </section>
        {radioValue === 'rollover-to-me' && (
          <section
            className="mt-10 px-7 rounded-1"
            style={{ backgroundColor: '#EFF2F4' }}
          >
            <div className="border-bottom pt-11 pb-7">
              <div>
                <h6 className={'fw-normal'}>Send it to me</h6>
              </div>
            </div>
            <Row className={'flex-column flex-lg-row m-0 mt-10 mt-lg-12 pb-13'}>
              <Col className="p-0 col-12 col-lg-4">
                <PreferedCard
                  className="border-light-gray"
                  id="pc-1"
                  title={'Home'}
                  isSwitch={false}
                  isDismissable={false}
                >
                  {
                    <div
                      className={
                        'fw-light text-fs-6 single-col d-flex flex-column'
                      }
                    >
                      {formatAddressInTwoLines(addresses.home[0]).map(
                        (line, index) => (
                          <span key={index}>{line}</span>
                        )
                      )}
                    </div>
                  }
                </PreferedCard>
              </Col>
            </Row>
          </section>
        )}
        {radioValue === 'letter-writing' && (
          <section
            className="mt-10 px-7 rounded-1"
            style={{ backgroundColor: '#EFF2F4' }}
          >
            <div className="border-bottom pt-11 pb-7">
              <div>
                <h6 className={'fw-normal'}>Send to Third Party</h6>
              </div>
              <div className={'fw-light text-sub1 text-light-grey-normal'}>
                <span>
                  Please select from the list of addresses added to your
                  profile.
                </span>
              </div>
            </div>
            <Row
              className={
                'flex-column flex-lg-row mt-10 mt-lg-11 pb-11 pb-lg-13'
              }
            >
              <form
                onSubmit={handleSubmit(handleSubmitForm)}
                noValidate={true}
                id={'address-form'}
              >
                <Row className="m-0">
                  <Col className={'p-0 col-12'} style={{ maxWidth: 412 }}>
                    <Controller
                      name={'thirdPartyName'}
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={'Third Party Name'}
                          placeholder={'Third Party Name'}
                          isInvalid={!!errors.thirdPartyName}
                        />
                      )}
                    />
                    {errors.thirdPartyName && (
                      <div className="error-message">
                        <span>{errors.thirdPartyName.message}</span>
                      </div>
                    )}
                  </Col>
                </Row>
                <Row className="m-0 mt-7">
                  <Col className={'p-0 ps-lg-0 pe-lg-5 col-12 col-lg-6'}>
                    <Controller
                      name={'address1'}
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={'Address 1'}
                          leftIcon={light('location-dot')}
                          placeholder={'Address'}
                          isInvalid={!!errors.address1}
                        />
                      )}
                    />

                    {errors.address1 && (
                      <div className="error-message">
                        <span>{errors.address1.message}</span>
                      </div>
                    )}
                  </Col>
                  <Col
                    className={
                      'p-0 mt-7 mt-lg-0 ps-lg-5 pe-lg-0 col-12 col-lg-6'
                    }
                  >
                    <Controller
                      name={'address2'}
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={'Address 2 (Optional)'}
                          leftIcon={light('location-dot')}
                          placeholder={'Address'}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row className="m-0 mt-7">
                  <Col className={'p-0 ps-lg-0 col-12 col-lg-4'}>
                    <Controller
                      name={'city'}
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={'City'}
                          placeholder={'City'}
                          isInvalid={!!errors.city}
                        />
                      )}
                    />

                    {errors.city && (
                      <div className="error-message">
                        <span>{errors.city.message}</span>
                      </div>
                    )}
                  </Col>
                  <Col className={'p-0 px-lg-5 mt-7 mt-lg-0 col-12 col-lg-4'}>
                    <Controller
                      name={'state'}
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label={'State'} options={states} />
                      )}
                    />

                    {errors.state && (
                      <div className="error-message">
                        <span>{errors.state.message}</span>
                      </div>
                    )}
                  </Col>
                  <Col
                    className={
                      'p-0 ps-lg-0 pe-lg-0 mt-7 mt-lg-0 col-12 col-lg-4'
                    }
                  >
                    <Controller
                      name={'zip'}
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          label={'ZIP Code'}
                          isInvalid={!!errors.zip}
                          maxLength={5}
                        />
                      )}
                    />

                    {errors.zip && (
                      <div className="error-message">
                        <span>{errors.zip.message}</span>
                      </div>
                    )}
                  </Col>
                </Row>
              </form>
            </Row>
          </section>
        )}
      </ModalBody>
      <Modal.Footer className="pt-12">
        <Button
          onClick={handleCloseModal}
          className="text-nowrap mt-7 mt-lg-0"
          variant="primary-outline"
        >
          Cancel
        </Button>
        <Button
          form={'address-form'}
          type={'submit'}
          variant="primary"
          onClick={handleConfirm}
        >
          Generate Letter
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PensionVerificationLetter;
